/**
 * Recuperação distribuída: leader persistido, verify-after-write, failover.
 */

import type { QueueItem } from '../queue';
import { reconcileProcessingOrphans } from './reconciliation';
import { releaseFlushLock, tryAcquireFlushLock } from './distributedLock';
import { LEADER_TTL_MS } from './coordinationConstants';
import { resolveLeaderConflict } from './conflictResolver';
import {
  invalidatePreviousOwner,
  clearInvalidationOnPromote,
} from './ownershipInvalidation';
import {
  recordOrphanProcessingReset,
  recordStaleLeaderRecovery,
  recordEpochChange,
  recordLeaderChange,
} from '../telemetry';

const LEADER_KEY = 'GOMARKE_TAB_LEADER';

export interface LeaderRecord {
  tabId: string;
  expiresAt: number;
  fencingToken: number;
  epoch: number;
}

function readLeaderRaw(): LeaderRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LEADER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LeaderRecord;
    return {
      ...parsed,
      epoch: parsed.epoch ?? 0,
    };
  } catch {
    return null;
  }
}

function writeLeader(candidate: LeaderRecord): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LEADER_KEY, JSON.stringify(candidate));
}

function verifyLeaderOwnership(expected: LeaderRecord): boolean {
  const current = readLeaderRaw();
  if (!current) return false;
  return (
    current.tabId === expected.tabId &&
    current.fencingToken === expected.fencingToken &&
    current.epoch === expected.epoch
  );
}

export function readLeader(): LeaderRecord | null {
  return readLeaderRaw();
}

export function isLeaderStale(): boolean {
  const leader = readLeaderRaw();
  if (!leader) return true;
  return leader.expiresAt <= Date.now();
}

export function getLeaderEpochFromStorage(): number {
  return readLeaderRaw()?.epoch ?? 0;
}

export function getLeaderFencingToken(): number {
  return readLeaderRaw()?.fencingToken ?? 0;
}

export function isPersistedLeader(tabId: string): boolean {
  const leader = readLeaderRaw();
  if (!leader || isLeaderStale()) return false;
  return leader.tabId === tabId;
}

export function claimLeaderIfStale(tabId: string): boolean {
  const now = Date.now();
  const existing = readLeaderRaw();
  if (existing && existing.expiresAt > now && existing.tabId !== tabId) {
    return false;
  }
  const stale = !existing || existing.expiresAt <= now;
  if (stale && existing) {
    invalidatePreviousOwner(existing.tabId, existing.epoch, existing.fencingToken);
  }
  const token = (existing?.fencingToken ?? 0) + 1;
  const epoch = (existing?.epoch ?? 0) + (stale ? 1 : 0);
  const candidate: LeaderRecord = {
    tabId,
    expiresAt: now + LEADER_TTL_MS,
    fencingToken: token,
    epoch,
  };
  writeLeader(candidate);
  if (!verifyLeaderOwnership(candidate)) return false;
  clearInvalidationOnPromote(tabId);
  if (stale) {
    recordStaleLeaderRecovery();
    recordEpochChange();
    recordLeaderChange();
  }
  return true;
}

export function runLeaderFailover(newTabId: string): boolean {
  const existing = readLeaderRaw();
  const now = Date.now();
  if (existing && existing.tabId !== newTabId) {
    invalidatePreviousOwner(existing.tabId, existing.epoch, existing.fencingToken);
    const winner = resolveLeaderConflict(
      {
        tabId: newTabId,
        epoch: (existing.epoch ?? 0) + 1,
        fencingToken: existing.fencingToken + 1,
      },
      {
        tabId: existing.tabId,
        epoch: existing.epoch,
        fencingToken: existing.fencingToken,
      }
    );
    if (winner === 'remote' && existing.expiresAt > now) return false;
  }
  const token = (existing?.fencingToken ?? 0) + 1;
  const epoch = (existing?.epoch ?? 0) + 1;
  const candidate: LeaderRecord = {
    tabId: newTabId,
    expiresAt: now + LEADER_TTL_MS,
    fencingToken: token,
    epoch,
  };
  writeLeader(candidate);
  if (!verifyLeaderOwnership(candidate)) return false;
  clearInvalidationOnPromote(newTabId);
  releaseFlushLock();
  tryAcquireFlushLock();
  recordStaleLeaderRecovery();
  recordEpochChange();
  recordLeaderChange();
  return true;
}

export function renewLeader(tabId: string): boolean {
  const leader = readLeaderRaw();
  if (!leader || leader.tabId !== tabId) return false;
  const renewed: LeaderRecord = {
    ...leader,
    expiresAt: Date.now() + LEADER_TTL_MS,
  };
  writeLeader(renewed);
  return verifyLeaderOwnership(renewed);
}

export function releaseLeader(tabId: string): void {
  const leader = readLeaderRaw();
  if (leader?.tabId === tabId && typeof window !== 'undefined') {
    window.localStorage.removeItem(LEADER_KEY);
  }
}

export function recoverAfterDeadLeader(
  queue: QueueItem[],
  tabId: string,
  aliveTabIds: Set<string>
): QueueItem[] {
  const orphans = queue.filter(
    i =>
      i.status === 'processing' &&
      i.processingTabId != null &&
      !aliveTabIds.has(i.processingTabId)
  );
  if (orphans.length > 0) recordOrphanProcessingReset(orphans.length);

  let next = reconcileProcessingOrphans(queue, aliveTabIds);
  if (isLeaderStale()) {
    claimLeaderIfStale(tabId);
    releaseFlushLock();
    tryAcquireFlushLock();
  }
  return next;
}

export function _testClearLeader(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem(LEADER_KEY);
  }
}
